import { ISiteGalleryItem } from '@api/models/site-gallery-item.model';
import { Button } from '@hyperflake/react-ui-library';
import { useProgressTabContext } from '@library/progress-tab';
import { ErrorMessage, Form, Formik } from 'formik';
import produce from 'immer';
import FileUploadPreview from 'modules/shared/components/FileUploadPreview/FileUploadPreview';
import DragAndDropFileUpload from 'modules/shared/components/FormFields/DragAndDropFileUpload';
import { convertFileSize, getFileExtension } from 'modules/shared/helpers/shared.utils';
import { uploadMultipleFilesSequentially } from 'modules/site-gallery/helper/multipart.utils';
import { FC } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

const FormSchema = Yup.object().shape({
    files: Yup.array().test('validate-files', function (files: File[]) {
        if (!files || files.length === 0) {
            return this.createError({ message: 'Please upload at least one file' });
        }

        if (files.length > 25) {
            return this.createError({
                message: 'A bulk upload of 25 files is possible at a time',
            });
        }

        return true;
    }),
});

interface ISiteGalleryAddDrawerForm {
    onClose: () => void;
    clientId: string;
    projectId: string;
    onUpload: (siteGallery: ISiteGalleryItem[]) => void;
}

const SiteGalleryAddDrawerForm: FC<ISiteGalleryAddDrawerForm> = (props) => {
    const { onClose, clientId, projectId, onUpload } = props;

    const progressTabCtx = useProgressTabContext();

    const handleSubmit = async (values: { files: File[] }) => {
        if (!values.files?.length) return;

        const totalSize = values.files.reduce((acc, cur) => acc + cur.size, 0);

        const abortController = new AbortController();

        const itemData = progressTabCtx.addItem({
            _id: uuidv4(),
            title: 'Uploading Files',
            text: convertFileSize(totalSize),
            extension: getFileExtension(values.files[0]?.name || ''),
            onCancel: () => abortController.abort(),
        });

        onClose();

        try {
            // T is the "complete response per file"
            // choose one:
            // 1) if complete returns ISiteGalleryItem:
            //    const perFileResults = await uploadMultipleFilesSequentially<ISiteGalleryItem>({...})
            // 2) if complete returns ISiteGalleryItem[]:
            //    const perFileResults = await uploadMultipleFilesSequentially<ISiteGalleryItem[]>({...})
            //
            // We'll support both by typing it as union:
            const perFileResults = await uploadMultipleFilesSequentially<ISiteGalleryItem | ISiteGalleryItem[]>({
                files: values.files,
                clientId,
                projectId,
                signal: abortController.signal,
                onOverallProgress: (progress) => {
                    progressTabCtx.updateItem(itemData._id, { progress });
                },
            });

            // flatten results to ISiteGalleryItem[]
            const flattened: ISiteGalleryItem[] = [];
            for (const r of perFileResults) {
                if (Array.isArray(r)) flattened.push(...r);
                else flattened.push(r);
            }

            progressTabCtx.updateItem(itemData._id, {
                progress: 100,
                isCompleted: true,
            });

            onUpload(flattened);
        } catch (err: any) {
            const isAborted = err?.name === 'AbortError' || err?.code === 'ERR_CANCELED';

            if (!isAborted) {
                toast.error(err?.response?.data?.message || err?.message || 'Some error occured, please try again.');

                progressTabCtx.updateItem(itemData._id, {
                    progress: 100,
                    isErrored: true,
                });
            }
        }
    };

    return (
        <div>
            <div>
                <h4>Add Gallery Items</h4>
            </div>

            <div className="h-full">
                <Formik
                    initialValues={{ files: [] }}
                    validationSchema={FormSchema}
                    enableReinitialize
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue }) => (
                        <Form className="flex flex-col flex-1">
                            <div className="mt-6">
                                <DragAndDropFileUpload
                                    text="Browse JPG/PNG/MP4"
                                    onChange={(files) => {
                                        if (values.files.length + files.length > 25) {
                                            toast.error('You can only add upto 25 files (photos or videos) at a time');
                                            return;
                                        }

                                        setFieldValue('files', [...values.files, ...files]);
                                    }}
                                    accept={{
                                        'image/*': ['.jpeg', '.jpg', '.png'],
                                        'video/*': [],
                                    }}
                                    multiple
                                />

                                <ErrorMessage component="div" className="form-error-message" name="files" />
                            </div>

                            <div className="space-y-4 mt-6">
                                {values.files.map((file, index) => (
                                    <FileUploadPreview
                                        key={index}
                                        name={file.name}
                                        size={file.size}
                                        onCancel={() => {
                                            const files = produce(values.files, (draftFiles) => {
                                                draftFiles.splice(index, 1);
                                            });

                                            setFieldValue('files', files);
                                        }}
                                    />
                                ))}
                            </div>

                            <div className="mt-auto">
                                <div className="flex gap-4 mt-8">
                                    <Button size="sm" className="font-medium" onClick={onClose}>
                                        Cancel
                                    </Button>

                                    <Button type="submit" size="sm">
                                        Upload
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default SiteGalleryAddDrawerForm;
